import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import Layout from './components/Layout/Layout';
import MobileLayout from './components/Layout/MobileLayout';
import TorontoMap from './components/Map/Map';
import Search from './components/Search/Search';
import LoadingScreen from './components/LoadingScreen/LoadingScreen';

import { useMobileDetection } from './hooks/useMobileDetection';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import AppChrome from './components/Chrome/AppChrome';
import { usePageTracking } from './hooks/usePageTracking';
import { trackPropertySearch } from './utils/analytics';
// import AuthButton from './components/Auth/AuthButton'; // Unused for now
import ProtectedFeature from './components/Auth/ProtectedFeature';
import SidePanel from './components/SidePanel/SidePanel';
import BottomSheet from './components/BottomSheet/BottomSheet';
import FloatingButtons from './components/FloatingButtons/FloatingButtons';
import { config, logConfig } from './utils/config';
import { restoreLayoutState } from './utils/storageUtils';
import { getSearchContext, clearSearchContext } from './utils/searchContextStorage';

import Legal from './pages/Legal';
import Pricing from './pages/Pricing';
import AccountPage from './pages/AccountPage';
import ReportsPage from './pages/ReportsPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LoginPage from './pages/LoginPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import EmailConfirmed from './pages/EmailConfirmed';
import Contact from './pages/Contact';
import HomePage from './pages/HomePage';
import MapPage from './pages/MapPage';
import Blog from './pages/Blog';
import PermitReview from './pages/PermitReview';

// Account page components
import OverviewPage from './pages/account/OverviewPage';
import AccountSettingsPage from './pages/account/AccountSettingsPage';
import AccountReportsPage from './pages/account/AccountReportsPage';
import AccountPricingPage from './pages/account/AccountPricingPage';

// Extend window interface for our global map resources
declare global {
  interface Window {
    mapResourcesReady?: boolean;
    mapPreRenderComplete?: boolean;
    preloadedZoningData?: any;
    preloadedBoundaryData?: any;
    debugZoneFinder?: (lat: number, lng: number) => any | null; // Added for debugging
  }
}

// Responsive Page Wrapper Component
interface ResponsivePageProps {
  children: React.ReactNode;
  showHeader?: boolean;
}

function ResponsivePage({ children, showHeader = true }: ResponsivePageProps) {
  const { isMobile, isTablet } = useMobileDetection();
  
  // Use mobile layout only for phones (≤768px) to match CSS breakpoints
  if (isMobile) {
    return (
      <MobileLayout showHeader={showHeader}>
        {children}
      </MobileLayout>
    );
  }
  
  return <>{children}</>;
}

function AppContent() {
  // Enable automatic page tracking
  usePageTracking();
  
  const location = useLocation();
  const navigate = useNavigate();
  
  const [displayUnit, setDisplayUnit] = useState<'m' | 'ft'>('m');
  const [mobileSidebarMinimized, setMobileSidebarMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [sidebarHeight, setSidebarHeight] = useState(40); // Current height as percentage
  const [selectedZone, setSelectedZone] = useState<any>(null);
  
  const [selectedParcelId, setSelectedParcelId] = useState<string | undefined>(undefined);
  
  // const [searchResultsVisible, setSearchResultsVisible] = useState<boolean>(false); // Unused for now
  
  const [interactionType, setInteractionType] = useState<'mapClick' | 'addressSearch'>('mapClick');
  
  const [point, setPoint] = useState<{lat: number, lng: number, type: 'mapClick' | 'addressSearch', address?: string} | null>(null);
  
  const [parcelData, setParcelData] = useState<any>(null);
  const [paymentSuccessMessage, setPaymentSuccessMessage] = useState<string | null>(null);
  const { isMobile, isTablet } = useMobileDetection();
  
  // Only use mobile layout for actual mobile screens (≤768px) to match CSS breakpoints
  const useMobileLayout = isMobile;
  
  
  // Check if resources are already loaded to avoid unnecessary loading screen
  const resourcesAlreadyLoaded = window.mapResourcesReady && window.preloadedZoningData && window.preloadedBoundaryData;
  const sessionLoadComplete = sessionStorage.getItem('zoningpal_resources_loaded') === 'true';
  
  // Mobile progressive loading: Skip initial loading screen, load in background
  const shouldUseProgressiveLoading = useMobileLayout;
  const [isAppLoading, setIsAppLoading] = useState(
    shouldUseProgressiveLoading ? false : (!resourcesAlreadyLoaded && !sessionLoadComplete)
  );
  const [loadingStage] = useState('Initializing...');
  
  // Progressive loading states for mobile
  const [isZoningDataLoading, setIsZoningDataLoading] = useState(false);
  const [zoningDataProgress, setZoningDataProgress] = useState(0);
  const [progressiveZoningData, setProgressiveZoningData] = useState<any>(null);
  const [progressiveBoundaryData, setProgressiveBoundaryData] = useState<any>(null);
  // Mobile layout uses integrated sidebar, no modal state needed
  
  const mapRef = useRef<any>(null);
  const { user } = useAuth();


  const zoneFinderRef = useRef<((lat: number, lng: number) => any | null) | null>(null);

  // Restore layout state after auth changes to prevent 'scroll up' appearance
  useEffect(() => {
    if (user && useMobileLayout) {
      // Attempt to restore layout state when user becomes available on mobile
      const restored = restoreLayoutState();
      if (restored) {
        console.log('📱 Layout state restoration attempted for mobile user');
      }
    }
  }, [user, useMobileLayout]);

  // Handle payment success and restore search context
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const paymentStatus = searchParams.get('payment');
    
    if (paymentStatus === 'success') {
      console.log('💳 Payment successful, checking for saved search context...');
      
      // Get saved search context
      const savedContext = getSearchContext();
      
      if (savedContext) {
        console.log('✅ Restoring search context:', savedContext.address);
        
        // Restore the search by triggering address select
        setTimeout(() => {
          handleAddressSelect(savedContext.lat, savedContext.lng, savedContext.address);
          
          // Show success message
          setPaymentSuccessMessage('Payment successful! Your search has been restored. You can now generate your report.');
          
          // Clear the saved context
          clearSearchContext();
          
          // Remove payment param from URL to clean up
          navigate('/map', { replace: true });
          
          // Auto-hide success message after 8 seconds
          setTimeout(() => {
            setPaymentSuccessMessage(null);
          }, 8000);
        }, 500); // Short delay to ensure map is ready
      } else {
        console.log('⚠️ No saved search context found');
        // Show generic success message without restoration
        setPaymentSuccessMessage('Payment successful! Your credits have been added to your account.');
        
        // Remove payment param from URL
        navigate('/map', { replace: true });
        
        // Auto-hide message after 5 seconds
        setTimeout(() => {
          setPaymentSuccessMessage(null);
        }, 5000);
      }
    } else if (paymentStatus === 'canceled') {
      console.log('❌ Payment was canceled');
      setPaymentSuccessMessage('Payment was canceled. Your search has been preserved.');
      
      // Check for saved context and restore even on cancel
      const savedContext = getSearchContext();
      if (savedContext) {
        setTimeout(() => {
          handleAddressSelect(savedContext.lat, savedContext.lng, savedContext.address);
        }, 500);
      }
      
      // Remove payment param from URL
      navigate('/map', { replace: true });
      
      // Auto-hide message after 5 seconds
      setTimeout(() => {
        setPaymentSuccessMessage(null);
      }, 5000);
    }
  }, [location.search]); // Only run when URL params change

  // Mobile layout automatically shows content in integrated sidebar

  // Load application assets and resources (mobile progressive vs desktop standard)
  useEffect(() => {
    // Helper function to fetch with timeout and retry logic
    const fetchWithTimeout = async (url: string, timeoutMs = 15000, maxRetries = 2): Promise<Response> => {
      let lastError: Error | null = null;
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`📡 Loading ${url} (attempt ${attempt}/${maxRetries})`);
          
          const controller = new AbortController();
          const timeoutId = setTimeout(() => {
            controller.abort();
          }, timeoutMs);

          const response = await Promise.race([
            fetch(url, { 
              signal: controller.signal,
              cache: 'no-cache', // Prevent stale cache issues on mobile
              headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
              }
            }),
            new Promise<never>((_, reject) => 
              setTimeout(() => reject(new Error(`Request timeout after ${timeoutMs}ms`)), timeoutMs)
            )
          ]);

          clearTimeout(timeoutId);
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          console.log(`✅ Successfully loaded ${url}`);
          return response;
          
        } catch (error: any) {
          lastError = error;
          console.warn(`❌ Attempt ${attempt}/${maxRetries} failed for ${url}:`, error.message);
          
          // If this is the last attempt, break out of the loop
          if (attempt === maxRetries) {
            break;
          }
          
          // Wait before retrying (exponential backoff)
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          console.log(`⏳ Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
      
      throw lastError || new Error(`Failed to fetch ${url} after ${maxRetries} attempts`);
    };

    // Mobile Progressive Loading - Load data in background while showing map
    const loadResourcesProgressively = async () => {
      try {
        console.log('📱 Mobile progressive loading started');
        setIsZoningDataLoading(true);
        setZoningDataProgress(10);
        
        // Load boundary data first (smaller file)
        console.log('🗺️ Loading boundary data...');
        setZoningDataProgress(20);
        
        try {
          const boundaryResponse = await fetchWithTimeout(`${config.TILES_URL}/data/toronto_boundary_extracted.json`, 10000, 2);
          const boundaryData = await boundaryResponse.json();
          setProgressiveBoundaryData(boundaryData);
          window.preloadedBoundaryData = boundaryData;
          console.log('✅ Boundary data loaded progressively');
          setZoningDataProgress(40);
        } catch (error) {
          console.warn('⚠️ Boundary data failed to load, continuing without it:', error);
        }

        // Load zoning data in background (large file)
        console.log('📊 Loading zoning data in background...');
        setZoningDataProgress(50);
        
        try {
          const zoningResponse = await fetchWithTimeout(`${config.TILES_URL}/data/complete-toronto-zoning.geojson`, 30000, 2);
          setZoningDataProgress(80);
          
          const zoningData = await zoningResponse.json();
          setProgressiveZoningData(zoningData);
          window.preloadedZoningData = zoningData;
          setZoningDataProgress(100);
          
          console.log('✅ Zoning data loaded progressively');
          
          // Mark resources as ready
          window.mapResourcesReady = true;
          sessionStorage.setItem('zoningpal_resources_loaded', 'true');
          
        } catch (error) {
          console.warn('⚠️ Zoning data failed to load, map will work without zoning overlay:', error);
          setZoningDataProgress(100);
        }
        
        setIsZoningDataLoading(false);
        console.log('✅ Mobile progressive loading complete');
        
      } catch (error) {
        console.error('❌ Mobile progressive loading error:', error);
        setIsZoningDataLoading(false);
        setZoningDataProgress(100);
      }
    };

    // Desktop Standard Loading - Original behavior with loading screen
    const loadResourcesStandard = async () => {
      try {
        // Global object to track the loaded map components
        const mapLoaded = { zoningData: false, boundaryData: false };
        
        // Pre-initialize map resources before showing the map
        window.mapResourcesReady = false;
        
        // Load all map and app resources in parallel with timeout protection
        const tasks = [
          // Load zoning data with timeout and retry
          fetchWithTimeout(`${config.TILES_URL}/data/complete-toronto-zoning.geojson`, 20000, 3)
            .then(async response => {
              console.log('📊 Processing zoning data...');
              const data = await response.json();
              window.preloadedZoningData = data;
              mapLoaded.zoningData = true;
              console.log('✅ Zoning data loaded and cached');
              return data;
            })
            .catch(error => {
              console.error('❌ Failed to load zoning data:', error);
              // Don't throw - allow app to continue with fallback
              return null;
            }),
          
          // Load boundary data with timeout and retry
          fetchWithTimeout(`${config.TILES_URL}/data/toronto_boundary_extracted.json`, 15000, 3)
            .then(async response => {
              console.log('🗺️ Processing boundary data...');
              const data = await response.json();
              window.preloadedBoundaryData = data;
              mapLoaded.boundaryData = true;
              console.log('✅ Boundary data loaded and cached');
              return data;
            })
            .catch(error => {
              console.error('❌ Failed to load boundary data:', error);
              // Don't throw - allow app to continue with fallback
              return null;
            }),
          
          // Minimum loading time to prevent flash
          new Promise(resolve => setTimeout(() => {
            resolve(true);
          }, 1000)) // Reduced from 1500ms for faster loading
        ];

        // Wait for all resources to load (or fail gracefully)
        const results = await Promise.all(tasks);
        
        // Check if critical resources loaded
        const zoningDataLoaded = results[0] !== null;
        const boundaryDataLoaded = results[1] !== null;
        
        if (!zoningDataLoaded && !boundaryDataLoaded) {
          throw new Error('Failed to load critical map resources');
        }
        
        if (!zoningDataLoaded || !boundaryDataLoaded) {
          console.warn('⚠️ Some resources failed to load, but continuing with available data');
        }
        
        // Signal that map resources are ready
        window.mapResourcesReady = true;
        
        // Brief pre-render stage just to ensure map elements load properly
        const quickPreRender = async () => {
          console.log('Starting quick pre-render stage');
          // Signal to map components that we're ready
          window.mapPreRenderComplete = true;
          
          // Just a quick pause to let the browser process
          await new Promise(resolve => setTimeout(resolve, 300));
        };
        
        // Run the quick pre-render stage
        await quickPreRender();
        
        // Mark resources as loaded for this session
        sessionStorage.setItem('zoningpal_resources_loaded', 'true');
        
        // Hide loading screen only after complete pre-rendering
        setIsAppLoading(false);
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('✅ App loading complete, resources cached for session');
        }
      } catch (error) {
        console.error('❌ Error during application initialization:', error);
        
        // Still try to make basic functionality available
        window.mapResourcesReady = true; // Allow map to render in basic mode
        
        // Clear any corrupted session storage
        try {
          sessionStorage.removeItem('zoningpal_resources_loaded');
        } catch (storageError) {
          console.warn('Failed to clear session storage:', storageError);
        }
        
        // Hide loading screen even on error - we'll show an error message in the UI if needed
        setIsAppLoading(false);
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('🚨 Loading completed with errors - app will attempt to function in degraded mode');
        }
      }
    };

    // Skip loading if resources are already available
    if (window.mapResourcesReady && window.preloadedZoningData && window.preloadedBoundaryData) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('🚀 Resources already loaded, skipping loading screen');
      }
      if (!shouldUseProgressiveLoading) {
        setIsAppLoading(false);
      }
      // For mobile progressive, set the data directly
      if (shouldUseProgressiveLoading) {
        setProgressiveZoningData(window.preloadedZoningData);
        setProgressiveBoundaryData(window.preloadedBoundaryData);
      }
      return;
    }

    // Also skip if session storage indicates resources were loaded
    if (sessionStorage.getItem('zoningpal_resources_loaded') === 'true') {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('🔄 Session indicates resources already loaded, skipping');
      }
      if (!shouldUseProgressiveLoading) {
        setIsAppLoading(false);
      }
      return;
    }

    if (shouldUseProgressiveLoading) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('📱 Starting mobile progressive loading...');
      }
      loadResourcesProgressively();
    } else {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('🖥️ Loading map resources for desktop (standard loading)...');
      }
      loadResourcesStandard();
    }
    
    logConfig(); // Log current config for debugging

  }, [shouldUseProgressiveLoading]);

// Callback to receive the zone finder function from Map component
  const handleZoneFinderReady = (finder: (lat: number, lng: number) => any | null) => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('Zone finder function received from Map component');
    }
    zoneFinderRef.current = finder;
  };

  // Handle zone click
  const handleZoneClick = (properties: any, _parcelId?: string) => {
    setSelectedZone(properties);
    setInteractionType('mapClick');
    setSelectedParcelId(undefined);
    setParcelData(null);
    if (properties && properties.lat && properties.lng) {
      setPoint({ lat: properties.lat, lng: properties.lng, type: 'mapClick' });
    }
  };

  const getMapRef = (map: any) => {

    mapRef.current = map;
  };

  const handleAddressSelect = async (lat: number, lng: number, displayName: string) => {
    // Track property search
    trackPropertySearch(displayName, { lat, lng });
    
    setSelectedZone(null);
    setSelectedParcelId(undefined);
    setParcelData(null);
    
    setInteractionType('addressSearch');
    setPoint({ lat, lng, type: 'addressSearch', address: displayName });

    if (mapRef.current) {
      
      // Use responsive zoom levels based on screen size
      const getResponsiveZoomLevel = () => {
        const width = window.innerWidth;
        if (width < 768) {
          return 17; // Mobile - slightly less zoomed
        } else if (width >= 768 && width < 1024) {
          return 16; // Tablet - less zoomed for better context
        } else {
          return 18; // Desktop - full zoom
        }
      };
      
      const zoomLevel = getResponsiveZoomLevel();
      
      // Temporarily disable bounds for tablet address search to prevent bouncing  
      // Use the isTablet from useMobileDetection hook (already available in scope)
      let originalMaxBounds = null;
      
      if (isTablet) {
        console.log('Tablet detected - temporarily disabling map bounds for smooth centering');
        originalMaxBounds = mapRef.current.options.maxBounds;
        mapRef.current.setMaxBounds(null); // Remove bounds temporarily
      }
      
      // For tablet mode, offset the center point upward to account for bottom UI elements
      let adjustedLat = lat;
      if (isTablet) {
        // In tablet mode, we need to account for:
        // 1. Property/Analysis tab buttons overlaying bottom of map
        // 2. Bottom sidebar taking up lower portion of screen
        // Offset by approximately 0.006 degrees latitude (roughly 600-700 meters north)
        adjustedLat = lat - 0.001;
        console.log(`Tablet mode: offsetting center from ${lat} to ${adjustedLat} to account for tab buttons and bottom sidebar`);
      }
      
      mapRef.current.flyTo([adjustedLat, lng], zoomLevel, {
        animate: true,
        duration: 2.5 // Increased from 1.5 to 2.5 seconds to give vector tiles time to load
      });
      
      // Restore bounds after flyTo completes for tablets
      if (isTablet && originalMaxBounds) {
        setTimeout(() => {
          if (mapRef.current) {
            console.log('Restoring map bounds after centering');
            mapRef.current.setMaxBounds(originalMaxBounds);
          }
        }, 2600); // Slightly longer than animation duration (2.5s + 0.1s buffer)
      }
      
      const LGlobal = window.L; 
      if (mapRef.current._searchMarker) {
        mapRef.current._searchMarker.remove();
      }
      mapRef.current._searchMarker = LGlobal.marker([lat, lng]).addTo(mapRef.current);
    } else {
      console.error('Map reference is null - cannot control zoom!');
    }

    const parcelDataPromise = Promise.race([
              fetch(`${config.API_URL}/api/parcel-by-point?lat=${lat}&lng=${lng}`)
        .then(response => response.ok ? response.json() : null),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Parcel data timeout')), 10000)
      )
    ]).catch(error => {
      console.error('Error fetching parcel data:', error);
      return null;
    });
    
    const zonePropertiesPromise = (async () => {
      try {

        const response = await fetch(`${config.API_URL}/api/zoning/spatial-data?lat=${lat}&lng=${lng}`);
        
        if (!response.ok) {
          console.error('Spatial API error:', response.status, response.statusText);
          return null;
        }
        
        const spatialData = await response.json();
        
        if (spatialData && spatialData.ZN_ZONE) {
          // The API already returns the correct format
          return spatialData;
        }
        
        return null;
      } catch (error) {
        console.error('Error fetching zone data from API for address search:', error);
        return null;
      }
    })();
    
    try {
      const [fetchedParcelData, zoneProps] = await Promise.all([parcelDataPromise, zonePropertiesPromise]);
      
      if (fetchedParcelData) {
        setParcelData(fetchedParcelData);
        setSelectedParcelId(fetchedParcelData?.parceli2);
      } else {
        setParcelData(null);
        setSelectedParcelId(undefined);
      }
      
      if (zoneProps) {
        setSelectedZone(zoneProps);
      } else {
        setSelectedZone(null);
      }
      
    } catch (error) {
      console.error('❌ Error in address selection:', error);
      setParcelData(null);
      setSelectedParcelId(undefined);
      setSelectedZone(null);
    }
  };
  
  const handleSearchStart = () => {
    setSelectedZone(null);
    setSelectedParcelId(undefined);
    setParcelData(null);
    setPoint(null);
    
    if (mapRef.current && mapRef.current._searchMarker) {
      mapRef.current._searchMarker.remove();
      mapRef.current._searchMarker = null;
    }
  };

  const handleAddressSelectSync = (lat: number, lng: number, displayName: string) => {
    handleAddressSelect(lat, lng, displayName).catch(error => {
      console.error('Error in address selection:', error);
    });
  };

  // Drag handlers for mobile sidebar
  const handleDragStart = (clientY: number) => {
    setIsDragging(true);
    setDragStartY(clientY);
    console.log('🖐️ Drag started at Y:', clientY);
  };

  const handleDragMove = (clientY: number) => {
    if (!isDragging) return;
    
    const deltaY = dragStartY - clientY; // Positive when dragging up
    const viewportHeight = window.innerHeight;
    const deltaPercent = (deltaY / viewportHeight) * 100;
    
    // Add some damping for smoother feel (like Google Maps)
    const dampingFactor = 0.8; // Slightly reduce sensitivity for smoother control
    let newHeight = sidebarHeight + (deltaPercent * dampingFactor);
    newHeight = Math.max(3, Math.min(80, newHeight)); // Constrain between 3% and 80%
    
    setSidebarHeight(newHeight);
    
    // Update minimized state based on height with smoother threshold
    setMobileSidebarMinimized(newHeight < 18); // Slightly higher threshold for better UX
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    console.log('🖐️ Drag ended at height:', sidebarHeight);
    
    // Improved snapping with smoother thresholds (like Google Maps)
    if (sidebarHeight < 20) { // More forgiving threshold
      setSidebarHeight(3);
      setMobileSidebarMinimized(true);
    } else {
      setSidebarHeight(40);
      setMobileSidebarMinimized(false);
    }
    
    // Recenter map after drag with longer delay to match the smoother transition
    setTimeout(() => {
      if (window.mapInstance && window.mapInstance.invalidateSize) {
        console.log('🗺️ Recentering map after sidebar change');
        window.mapInstance.invalidateSize();
      }
    }, 600); // Increased delay to match the 0.5s transition
  };

  // Global drag event listeners
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e: MouseEvent) => handleDragMove(e.clientY);
    const handleMouseUp = () => handleDragEnd();
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      handleDragMove(e.touches[0].clientY);
    };
    const handleTouchEnd = () => handleDragEnd();
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, dragStartY, sidebarHeight]);

  if (isAppLoading) {
    return <LoadingScreen stage={loadingStage} />;
  }

  const mapComponent = (
          <TorontoMap
            onZoneClick={handleZoneClick}
            internalMapRef={getMapRef}
            onZoneFinderReady={handleZoneFinderReady}
            selectedZone={selectedZone}
            displayUnit={displayUnit}
            showPropertyLines={true}
            parcelData={parcelData}
            // Progressive loading props for mobile
            progressiveZoningData={shouldUseProgressiveLoading ? progressiveZoningData : undefined}
            progressiveBoundaryData={shouldUseProgressiveLoading ? progressiveBoundaryData : undefined}
            isProgressiveLoading={shouldUseProgressiveLoading && isZoningDataLoading}
            progressiveLoadingProgress={shouldUseProgressiveLoading ? zoningDataProgress : undefined}
            // Pass mobile layout state to ensure legend matches sidebar behavior
            isMobileLayout={useMobileLayout}
          />
  );

  const sidebarComponent = (
    <div className="sidebar-content">
      {/* Desktop search section - only show on desktop */}
      {!useMobileLayout && (
        <div className="search-section">
          <Search
            onAddressSelect={handleAddressSelectSync}
            onSearchStart={handleSearchStart}
            onSearchResultsVisibilityChange={() => {}} 
          />
        </div>
      )}

      <div className="main-panels">
        <SidePanel
          selectedZone={selectedZone}
          userRole="Comprehensive"
          parcelData={parcelData}
          detailedParcelData={null}
          parcelDetailsLoading={false}
          parcelDetailsError={null}
          displayUnit={displayUnit}
          setDisplayUnit={setDisplayUnit}
          interactionType={interactionType}
          point={point}
          parcelId={selectedParcelId}
        />
      </div>
    </div>
  );
  
  const mobileSearchComponent = useMobileLayout ? (
    <div className="mobile-map-search-overlay">
      <Search
        onAddressSelect={handleAddressSelectSync}
        onSearchStart={handleSearchStart}
        onSearchResultsVisibilityChange={() => {}} 
      />
    </div>
  ) : null;

  // Success notification component
  const successNotification = paymentSuccessMessage ? (
    <div style={{
      position: 'fixed',
      top: useMobileLayout ? '80px' : '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: '#10b981',
      color: 'white',
      padding: '16px 24px',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      zIndex: 10000,
      maxWidth: useMobileLayout ? '90%' : '500px',
      textAlign: 'center',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: '14px',
      fontWeight: 500,
      animation: 'slideDown 0.3s ease-out'
    }}>
      {paymentSuccessMessage}
      <button
        onClick={() => setPaymentSuccessMessage(null)}
        style={{
          marginLeft: '12px',
          background: 'transparent',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          fontSize: '18px',
          fontWeight: 'bold',
          padding: '0 4px'
        }}
      >
        ×
      </button>
    </div>
  ) : null;

  // Mobile vs Desktop Layout (includes tablets)
  if (useMobileLayout) {
    // Determine if we have content to display
    const hasZoningContent = selectedZone || parcelData;
    const contentClass = hasZoningContent ? 'has-content' : 'no-content';
    
          return (
        <MobileLayout isMapPage={true}>
          {successNotification}
          <div className="mobile-app-content">
            {/* Mobile Map Section - Dynamic sizing */}
            <div className={`mobile-map-section ${contentClass} ${mobileSidebarMinimized ? 'sidebar-minimized' : ''} ${isDragging ? 'dragging' : ''}`}>
              {mapComponent}
              {/* Mobile search overlay on top of map */}
              {mobileSearchComponent}
            </div>
            
            {/* Mobile Sidebar - Integrated layout, not modal */}
            <div 
              className={`mobile-sidebar-section ${contentClass} ${mobileSidebarMinimized ? 'minimized' : ''} ${isDragging ? 'dragging' : ''}`}
              style={!mobileSidebarMinimized ? { height: `${sidebarHeight}vh` } : {}}
            >
              {/* Draggable overlay for the existing gray handle */}
              <div 
                className="mobile-sidebar-handle-overlay"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleDragStart(e.clientY);
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  handleDragStart(e.touches[0].clientY);
                }}
                onClick={() => {
                  if (!isDragging) {
                    console.log('🎯 Toggling sidebar - current minimized:', mobileSidebarMinimized);
                    setMobileSidebarMinimized(!mobileSidebarMinimized);
                    setSidebarHeight(mobileSidebarMinimized ? 40 : 3);
                    
                    // Recenter map after toggle with smooth timing
                    setTimeout(() => {
                      if (window.mapInstance && window.mapInstance.invalidateSize) {
                        console.log('🗺️ Recentering map after toggle');
                        window.mapInstance.invalidateSize();
                      }
                    }, 600); // Match the smoother 0.5s transition
                  }
                }}
                style={{
                  position: 'absolute',
                  top: '0px', // Extend to the very top edge of the bottom bar
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '80px', // Much narrower - just around the handle area
                  height: '32px', // Taller to cover from top edge to below handle
                  zIndex: 10,
                  cursor: isDragging ? 'grabbing' : 'grab',
                  touchAction: 'none',
                  // Remove grey highlight on tap/click
                  WebkitTapHighlightColor: 'transparent',
                  backgroundColor: 'transparent',
                  outline: 'none',
                  border: 'none'
                }}
              />
              {sidebarComponent}
            </div>
          </div>
        </MobileLayout>
      );
  }

  // Desktop Layout (unchanged)
  return (
    <div className="App">
      {successNotification}
      <Layout map={mapComponent} sidebar={sidebarComponent} />
      <FloatingButtons />
    </div>
  );
}

// Memoize the heavy Map component to prevent unnecessary re-renders
const MemoizedMap = React.memo(TorontoMap);

function App() {
  // Clean up any legacy soft launch access data
  useEffect(() => {
    localStorage.removeItem('softlaunch_access');
  }, []);

  return (
    <AuthProvider>
      <LanguageProvider>
        <Router>
          <AppChrome />
          <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/map" element={<AppContent />} />
          <Route path="/login" element={
            <ResponsivePage>
              <LoginPage />
            </ResponsivePage>
          } />
          <Route path="/auth/callback" element={
            <ResponsivePage showHeader={false}>
              <AuthCallbackPage />
            </ResponsivePage>
          } />
          <Route path="/email-confirmed" element={
            <ResponsivePage>
              <EmailConfirmed />
            </ResponsivePage>
          } />
          <Route path="/reset-password" element={
            <ResponsivePage>
              <ResetPasswordPage />
            </ResponsivePage>
          } />
          <Route path="/legal" element={
            <ResponsivePage>
              <Legal />
            </ResponsivePage>
          } />
          <Route path="/pricing" element={
            <ResponsivePage>
              <Pricing />
            </ResponsivePage>
          } />
          <Route path="/blog" element={
            <ResponsivePage>
              <Blog />
            </ResponsivePage>
          } />
          <Route path="/contact" element={
            <ResponsivePage>
              <Contact />
            </ResponsivePage>
          } />
          <Route path="/permitreview" element={
            <ResponsivePage>
              <PermitReview />
            </ResponsivePage>
          } />
          
          {/* New Account Routes with Sidebar */}
          <Route path="/account" element={
            <ResponsivePage>
              <OverviewPage />
            </ResponsivePage>
          } />
          <Route path="/account/overview" element={
            <ResponsivePage>
              <OverviewPage />
            </ResponsivePage>
          } />
          <Route path="/account/settings" element={
            <ResponsivePage>
              <AccountSettingsPage />
            </ResponsivePage>
          } />
          <Route path="/account/reports" element={
            <ResponsivePage>
              <AccountReportsPage />
            </ResponsivePage>
          } />
          <Route path="/account/pricing" element={
            <ResponsivePage>
              <AccountPricingPage />
            </ResponsivePage>
          } />
          
          {/* Legacy Routes - Keep for backward compatibility */}
          <Route path="/account-old" element={
            <ResponsivePage>
              <AccountPage />
            </ResponsivePage>
          } />
          <Route path="/reports" element={
            <ResponsivePage>
              <ReportsPage />
            </ResponsivePage>
          } />
          </Routes>
        </Router>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
